import React from 'react';
import { htnDrugInfluenceGroups } from './HtnDrugInfluenceTooltip';
import './NephronDiagram.css';

// Nephron sub-tooltip (2026-07-25). Ported from the Claude Design "Combat Hover
// Grammar" kidney sub-tooltip and AUGMENTED from three diuretic segments to the
// full FIVE, so no nephron region is left an orphan:
//   proximal tubule (carbonic-anhydrase inhibitors) — was a grey structural
//     connector in the design; now a real coloured segment.
//   descending limb (osmotic diuretics) — split off the loop's down-stroke.
//   loop of Henle / distal tubule / collecting duct — unchanged from the design.
//
// GAME-FUNCTIONAL CONTRACT (reconcile-audit wd8rh6nch): the figure's LOAD-BEARING
// layer is SEGMENT COVERAGE — which segments carry an equipped diuretic this round.
// Distinct covered segments are what drive the cross-segment diuresis synergy in
// the engine (diuresis_flat_per_enemy). The ion arrows (Na/K/Ca/Mg/HCO3/H2O) are
// CLINICAL mechanism-of-action flavour: they teach the pharmacology, they are NOT
// combat numbers. Serum potassium is a WHOLE-REGIMEN axis owned by the Potassium
// status tag, so it is NOT drawn here as a per-segment combat effect (only as the
// clinical K arrow the drug's MOA produces at that site).
//
// A segment with no equipped diuretic goes INACTIVE: its legend row dims with a
// hollow dot and its tubule limb drops to a muted dashed path — identical grammar
// to the design's partial-blockade variants.

export const NEPHRON_SEGMENT_COUNT = 5;

// Top-to-bottom down the nephron. `paths` are the design's SVG (viewBox 0 0 120
// 150); proximal reuses the old grey connector path, and the loop's single limb is
// split into a descending down-stroke (osmotic) + the thick ascending limb (loop).
// Tints hold the design's diuresis-teal identity, extended to a 5-step ramp
// (lightest at the top of the nephron, darkest at the collecting duct).
const NEPHRON_SEGMENTS = [
  {
    key: 'proximal',
    region: 'Proximal Tubule',
    tint: 'var(--seg-pct)',
    paths: ['M30 39 C30 50 44 46 46 60'],
    ions: [['Na⁺', 'down'], ['HCO₃⁻', 'down'], ['K⁺', 'down']],
  },
  {
    key: 'descending',
    region: 'Descending Limb',
    tint: 'var(--seg-osm)',
    paths: ['M46 60 L46 116'],
    ions: [['Na⁺', 'down'], ['H₂O', 'down']],
  },
  {
    key: 'loop',
    region: 'Loop Of Henle',
    tint: 'var(--seg-loop)',
    paths: ['M46 116 C46 130 68 130 68 116 L68 66'],
    ions: [['Na⁺', 'down'], ['K⁺', 'down'], ['Ca²⁺', 'down'], ['Mg²⁺', 'down']],
  },
  {
    key: 'distal',
    region: 'Distal Tubule',
    tint: 'var(--seg-thz)',
    paths: ['M68 66 C68 54 80 58 82 48 C84 40 92 42 92 52'],
    ions: [['Na⁺', 'down'], ['K⁺', 'down'], ['Ca²⁺', 'up']],
  },
  {
    key: 'collecting',
    region: 'Collecting Duct',
    tint: 'var(--seg-ksp)',
    paths: ['M92 52 L92 138 L86 144 L98 144'],
    ions: [['Na⁺', 'down'], ['K⁺', 'up']],
  },
];

const DOWN_ARROW = ['M12 19l0 -14', 'M18 13l-6 6', 'M6 13l6 6'];
const UP_ARROW = ['M12 5l0 14', 'M18 11l-6 -6', 'M6 11l6 -6'];

function IonArrow({ label, dir }) {
  return (
    <span className={`gt-kidney__ion gt-kidney__ion--${dir}`}>
      {label}
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {(dir === 'up' ? UP_ARROW : DOWN_ARROW).map((d) => <path key={d} d={d} />)}
      </svg>
    </span>
  );
}

// Map segment key -> the equipped diuretic drug name(s) covering it. Derived from
// the SAME htnDrugInfluenceGroups source the Diuresis "Fed by" ledger uses, so the
// figure can never disagree with the drain ledger about which drug sits where.
function segmentDrugNames(activeEquippedEffects) {
  const bySegment = new Map();
  htnDrugInfluenceGroups(activeEquippedEffects, 'diuresis').forEach((group) => {
    const seg = String(group.nephronSegment || '').trim();
    if (!seg) return;
    if (!bySegment.has(seg)) bySegment.set(seg, []);
    bySegment.get(seg).push(group.drugName);
  });
  return bySegment;
}

// The set of nephron segment keys that carry at least one equipped diuretic — the
// single source for both the active/inactive figure state and the "N Of 5 Active"
// count, so the two always agree.
export function activeNephronSegmentKeys(activeEquippedEffects) {
  return new Set(segmentDrugNames(activeEquippedEffects).keys());
}

// A compact drug label for a segment row: one name, or "First +N" for a stacked
// same-segment loadout (kept nowrap-safe like the design's __drug line).
function drugLabel(names = []) {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  return `${names[0]} +${names.length - 1}`;
}

export default function NephronDiagram({ activeEquippedEffects = [] }) {
  const bySegment = segmentDrugNames(activeEquippedEffects);

  return (
    <span className="gt-kidney">
      <span className="gt-kidney__fig" aria-hidden="true">
        <svg viewBox="0 0 120 150" fill="none">
          {/* Glomerulus — always structural (no drug acts on it). */}
          <circle cx="30" cy="30" r="9" fill="none" stroke="rgba(148,163,184,0.6)" strokeWidth="2" />
          {NEPHRON_SEGMENTS.flatMap((seg) => {
            const active = bySegment.has(seg.key);
            return seg.paths.map((d) => (
              <path
                key={d}
                className={active ? undefined : 'gt-kidney__path--off'}
                d={d}
                stroke={active ? seg.tint : undefined}
                strokeWidth="3.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ));
          })}
        </svg>
      </span>
      <span className="gt-kidney__legend">
        {NEPHRON_SEGMENTS.map((seg) => {
          const names = bySegment.get(seg.key) || [];
          const active = names.length > 0;
          return (
            <span
              key={seg.key}
              className={`gt-kidney__seg${active ? '' : ' gt-kidney__seg--off'}`}
            >
              <span className="gt-kidney__dot" style={{ background: seg.tint }} />
              <span className="gt-kidney__txt">
                <span className="gt-kidney__region">{seg.region}</span>
                {active ? (
                  <>
                    <span className="gt-kidney__drug">{drugLabel(names)}</span>
                    <span className="gt-kidney__eff">
                      {seg.ions.map(([label, dir]) => (
                        <IonArrow key={label} label={label} dir={dir} />
                      ))}
                    </span>
                  </>
                ) : (
                  <span className="gt-kidney__eff gt-kidney__eff--off">Inactive</span>
                )}
              </span>
            </span>
          );
        })}
      </span>
    </span>
  );
}
